import { FC, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Pill } from 'components/Pill/Pill'
import { getUserData } from 'redux/reducers/users'
import { MyDropdown } from 'components/ReactHookForm/MyDropdown'
import { Button } from 'components/Button/Button'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { MyDialog } from 'components/MyDialog'
import { OrderMSNForm } from 'models/claims/OrderMSNForm'
import { Alert } from '@cmsgov/ds-medicare-gov'
import { MySpinner } from 'components/MySpinner/MySpinner'
import { useSelector } from 'react-redux'
import { OrderMsnReason, ClaimDetailsRequest, OrderMsnResponse } from '@cms-max/medicare-api-sdk'
import { getOrderMsnRequest } from 'models/claims'
import { medicareApiClient } from 'clients/medicareApiClient'
import { ILoadingStatus } from 'models/LoadingStatus'
import { useMountedState } from 'hooks/useMountedState'
import { useModalCloseEvent } from 'components/MyDialog/useModalCloseEvent'

export type OrderMSNModalProps = {
  onClose: () => void,
  request: ClaimDetailsRequest,
  segmentInd: string
}

export const OrderMSNModal: FC<OrderMSNModalProps> = ({ onClose, request, segmentInd }) => {
  const { t } = useTranslation('claimsSearch', { keyPrefix: 'claimDetails' })
  const userData = useSelector(getUserData)
  const [orderMsnResponse, setOrderMsnResponse] = useState<OrderMsnResponse | undefined>(undefined)
  const [loadingState, setLoadingState] = useMountedState<ILoadingStatus>('standby')

  const methods = useForm<OrderMSNForm>({
    mode: 'onTouched',
    reValidateMode: 'onChange'
  })

  const onSubmit: SubmitHandler<OrderMSNForm> = useCallback(
    async (formValues: OrderMSNForm) => {
      setLoadingState('loading')
      try {
        const resp = await medicareApiClient.asyncOrderMsn(getOrderMsnRequest(request, segmentInd, formValues))

        if (resp) {
          setLoadingState('finished')
          setOrderMsnResponse(resp)
        } else {
          setLoadingState('error')
        }
      } catch (error) {
        setLoadingState('error')
      }
    }, [request, segmentInd, setLoadingState])

  const handleClose = useModalCloseEvent('claims', 'claims_duplicatemsncancel_button', onClose)

  return (
    <MyDialog
      heading={t('orderMSN.modalHeading')}
      containerId='medicare-my-claims-order-msn'
      closeButtonAriaLabel={t('orderMSN.closeButton')}
      onClose={handleClose}
      width='full'
    >
      <>
        <h2 className='ds-text-heading--2xl ds-u-margin-top--0 ds-u-margin-bottom--2'>{t('orderMSN.subtitle')}</h2>
        {(!orderMsnResponse) && (
          <>
            <p className='my-u-pb--8'>{t('orderMSN.requiredFieldsNote')}</p>
            <FormProvider {...methods}>
              <form onSubmit={methods.handleSubmit(onSubmit)}>
                <div className="my-u-mb--14">
                  <div>
                    <h3 className='ds-text-heading--xl'>{t('orderMSN.claimNumber')}</h3>
                    <Pill>{request.claimNumber}</Pill>
                    <h3 className='ds-text-heading--xl ds-u-margin-top--1'>{t('orderMSN.currentAddress')}</h3>
                    <Pill>
                      {userData.user?.mailingAddress?.address1 && (
                        <>
                          <span>{userData.user.mailingAddress.address1}</span>
                          <br />
                        </>
                      )}
                      {userData.user?.mailingAddress?.address2 && (
                        <>
                          <span>{userData.user.mailingAddress.address2}</span>
                          <br />
                        </>
                      )}
                      {userData.user?.mailingAddress?.city && (
                        userData.user.mailingAddress.city + ', '
                      )}
                      {userData.user?.mailingAddress?.state && (
                        userData.user.mailingAddress.state + ' '
                      )}
                      {userData.user?.mailingAddress?.zip && (
                        userData.user.mailingAddress.zip
                      )}
                    </Pill>
                  </div>
                  <div className='ds-u-margin-top--3'>
                    <MyDropdown
                      name='reason'
                      label={t('orderMSN.reason')}
                      labelClassName='ds-text-heading--xl ds-u-margin-y--1'
                      options={[
                        { value: OrderMsnReason.CopyNeededForOtherCarrier, label: t('orderMSN.reasonCopy') },
                        { value: OrderMsnReason.DuplicateForMyRecords, label: t('orderMSN.reasonDuplicate') },
                        { value: OrderMsnReason.LostOriginalCopy, label: t('orderMSN.reasonLost') },
                        { value: OrderMsnReason.NeverReceivedOriginalCopy, label: t('orderMSN.reasonNeverReceived') },
                        { value: OrderMsnReason.OriginalSentBackForAppeal, label: t('orderMSN.reasonAppeal') },
                        { value: OrderMsnReason.Other, label: t('orderMSN.reasonOther') }
                      ]}
                    />
                  </div>
                  <div className='ds-u-margin-top--3'>
                    <Alert role='alert' hideIcon={true} headingLevel='3' heading={t('orderMSN.MSNNote')}>
                      <p className="ds-c-alert__text">{t('orderMSN.MSNText')}</p>
                    </Alert>
                  </div>
                </div>
                {loadingState === 'error' && (
                  <Alert variation='error' role='alert' hideIcon={true} headingLevel='3' heading={t('orderMSN.errorHeading')}>
                    <p>
                      {t('orderMSN.submitFailure')}
                    </p>
                  </Alert>
                )}
                {loadingState === 'loading' && (
                  <MySpinner />
                )}
                {!(loadingState === 'loading') && (
                  <>
                    <div className='ds-c-dialog__actions'>
                      <div className='ds-l-form-row'>
                        <div className='ds-l-col--12 ds-l-sm-col--auto my-u-my--2'>
                          <Button
                            type='submit'
                            className='ds-c-button ds-c-button--solid my-u-py--6 my-u-width--full'
                            siteActivityProps={{
                              core: {
                                component: 'Claims',
                                elementId: 'claims_duplicatemsnsubmit_click',
                                coreClickData: {
                                  linkText: t('orderMSN.submit')
                                }
                              }
                            }}
                          >
                            {t('orderMSN.submit')}
                          </Button>
                        </div>
                        <div className='ds-l-col--12 ds-l-sm-col--auto my-u-my--2'>
                          <Button
                            onClick={onClose}
                            className='ds-c-button my-u-py--6 my-u-width--full'
                            siteActivityProps={{
                              core: {
                                component: 'Claims',
                                elementId: 'claims_duplicatemsncancel_click',
                                coreClickData: {
                                  linkText: t('orderMSN.cancel')
                                }
                              }
                            }}
                          >
                            {t('orderMSN.cancel')}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </form>
            </FormProvider>
          </>
        )}
        {loadingState === 'finished' && (
          <>
            {orderMsnResponse?.status === 'Failed' && (
              <>
                <Alert variation='error' role='alert' hideIcon={true} headingLevel='3' heading={t('orderMSN.statusHeader')}>
                  <p className="ds-c-alert__text">{t('orderMSN.failedMessage')}</p>
                </Alert>
              </>
            )}
            {orderMsnResponse?.status === 'Successful' && (
              <>
                <Alert variation='success' role='alert' hideIcon={true} headingLevel='3' heading={t('orderMSN.statusHeader')}>
                  <p className="ds-c-alert__text">{t('orderMSN.successfulMessage')}</p>
                </Alert>
              </>
            )}
            {orderMsnResponse?.status === 'Request Waiting' && (
              <>
                <Alert variation='success' role='alert' hideIcon={true} headingLevel='3' heading={t('orderMSN.statusHeader')}>
                  <p className="ds-c-alert__text">{t('orderMSN.requestWaitingMessage')}</p>
                </Alert>
              </>
            )}
            {orderMsnResponse?.status === 'Allow More Time' && (
              <>
                <Alert variation='success' role='alert' hideIcon={true} headingLevel='3' heading={t('orderMSN.statusHeader')}>
                  <p className="ds-c-alert__text">{t('orderMSN.allowMoreTimeMessage')}</p>
                </Alert>
              </>
            )}
          </>
        )}
      </>
    </MyDialog>
  )
}
